import {useMemo} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {AppendixTablePage} from '@/pages/components/audit-program/appendix/types';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface AppendixTableViewProps {
    header: AuditProgramSectionVO;
    page: AppendixTablePage;
    refCode: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.7rem', py: 0.6, px: 0.35, lineHeight: 1.25};
const bodySx = {...cellBorder, py: 0.35, px: 0.3, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** Blank working table for appendix pages (e.g. App2.4, App3.5). */
export default function AppendixTableView({
    header,
    page,
    refCode,
    values,
    onChange,
}: AppendixTableViewProps) {
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, page.blankRows)}, (_, i) => `r${i + 1}`),
        [page.blankRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {page.title}
            </Typography>

            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 960, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} style={{width: 32}}/>
                            {page.columns.map((c) => (
                                <TableCell key={c.id} sx={headSx}>{c.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id, idx) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" color="text.secondary">{idx + 1}</Typography>
                                </TableCell>
                                {page.columns.map((c) => (
                                    <TableCell key={c.id} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            sx={fieldSx}
                                            value={values[`${id}.${c.id}`] ?? ''}
                                            onChange={(e) => onChange(`${id}.${c.id}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
